import React, { useState, useEffect, useRef, useCallback } from 'react';

function EyeTracker({ width, height }) {
  const containerRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const [pupilOffset, setPupilOffset] = useState({ x: 0, y: 0 });
  
  const eyeRadius = Math.min(width, height) * 0.34;
  const pupilRadius = eyeRadius * 0.56;
  const maxPupilMove = eyeRadius - pupilRadius - 2;
  
  const eye1X = width * 0.3;
  const eye2X = width * 0.7;
  const eyeY = height * 0.5;
  
  const flushPupilPosition = useCallback(() => {
    rafRef.current = null;
    const { x: clientX, y: clientY } = pointerRef.current;
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const dx = clientX - centerX;
    const dy = clientY - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) {
      setPupilOffset({ x: 0, y: 0 });
      return;
    }

    const clampedDistance = Math.min(distance, 200);
    const normalizedDistance = clampedDistance / 200;
    const moveAmount = normalizedDistance * maxPupilMove;

    const angle = Math.atan2(dy, dx);
    const offsetX = Math.cos(angle) * moveAmount;
    const offsetY = Math.sin(angle) * moveAmount;

    setPupilOffset({ x: offsetX, y: offsetY });
  }, [maxPupilMove]);

  const updatePupilPosition = useCallback((clientX, clientY) => {
    pointerRef.current = { x: clientX, y: clientY };
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(flushPupilPosition);
  }, [flushPupilPosition]);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      updatePupilPosition(e.clientX, e.clientY);
    };
    
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        updatePupilPosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [updatePupilPosition]);
  
  return (
    <div 
      ref={containerRef}
      className="eye-tracker"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        style={{ position: 'absolute', top: 0, right: 0 }}
      >
        <circle
          cx={eye1X}
          cy={eyeY}
          r={eyeRadius}
          fill="white"
          stroke="#333"
          strokeWidth="1"
        />
        <circle
          cx={eye1X + pupilOffset.x}
          cy={eyeY + pupilOffset.y}
          r={pupilRadius}
          fill="#222"
          style={{ transition: 'cx 0.05s ease, cy 0.05s ease' }}
        />
        
        <circle
          cx={eye2X}
          cy={eyeY}
          r={eyeRadius}
          fill="white"
          stroke="#333"
          strokeWidth="1"
        />
        <circle
          cx={eye2X + pupilOffset.x}
          cy={eyeY + pupilOffset.y}
          r={pupilRadius}
          fill="#222"
          style={{ transition: 'cx 0.05s ease, cy 0.05s ease' }}
        />
      </svg>
    </div>
  );
}

export default EyeTracker;
